import { Dispatch, SetStateAction } from 'react';
import { analytics, SearchParams } from '@/constants/common';
import { useCommonFetchers } from '@/hooks/useCommonFetchers';
import { GroupedMaterialsType, Material } from '@/types/materialsLibrary';
import {
  apiMaterialFiles,
  apiMaterialFilesV2,
  apiMaterialFolderFiles,
  apiMaterialFolders,
  apiMaterialsFilters,
} from '@/utils/apiUrls';
import { SearchParams as SearchParamsType } from '@/types/common';

export const useMaterialsFetchers = () => {
  const { fetcher } = useCommonFetchers();

  const setFavoriteMaterial = async (
    id: string,
    contentName: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
  ) => {
    return fetcher(apiMaterialFiles(id), setIsLoading, { method: 'POST' }).then(() => {
      analytics.track('Material added to favorites', {
        value: contentName,
      });
    });
  };

  const removeFavoriteMaterial = async (
    id: string,
    contentName: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
  ) => {
    return fetcher(apiMaterialFiles(id), setIsLoading, { method: 'DELETE' }).then(() => {
      analytics.track('Material removed from favorites', {
        value: contentName,
      });
    });
  };

  const setFavoriteFolder = async (
    id: string,
    contentName: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
  ) => {
    return fetcher(apiMaterialFolders(id), setIsLoading, { method: 'POST' }).then(() => {
      analytics.track('Folder added to favorites', {
        value: contentName,
      });
    });
  };

  const removeFavoriteFolder = async (
    id: string,
    contentName: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
  ) => {
    return fetcher(apiMaterialFolders(id), setIsLoading, { method: 'DELETE' }).then(() => {
      analytics.track('Folder removed from favorites', {
        value: contentName,
      });
    });
  };

  const groupMaterialsByParentFolder = (materials: Material[]): GroupedMaterialsType => {
    const grouped: GroupedMaterialsType = {};

    materials.forEach((material) => {
      if (material.material_file_contents && material.material_file_contents.length > 0) {
        const parentId = material.parent_material_folder.id;
        if (!grouped[parentId]) {
          grouped[parentId] = {
            folderDetails: material.parent_material_folder,
            materials: [],
          };
        }
        grouped[parentId].materials.push(material);
      }
    });
    return grouped;
  };

  const fetchAndGroupMaterials = async (
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    needsGrouping: boolean = false,
  ) => {
    const data = await fetcher(apiMaterialFilesV2(), setIsLoading);
    if (needsGrouping) {
      const groupedResults = groupMaterialsByParentFolder(data);

      return groupedResults;
    } else {
      return data.data;
    }
  };

  const fetchMaterialFolderFiles = async (
    filter: SearchParamsType,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    signal?: AbortSignal,
  ) => {
    return fetcher(apiMaterialFolderFiles(filter), setIsLoading, { signal });
  };

  const fetchAndGroupMaterialsNewApi = async (
    filter: SearchParamsType,
    needsGrouping: boolean = false,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    signal: AbortSignal,
  ) => {
    const data = await fetcher(
      apiMaterialFolderFiles({ [SearchParams.filterMaterialFolderHasParents]: 'false', ...filter }),
      setIsLoading,
      { signal },
    );
    if (needsGrouping) {
      return groupMaterialsByParentFolder(data);
    } else {
      return data;
    }
  };

  const fetchFilters = async (setIsLoading: Dispatch<SetStateAction<boolean>>) => {
    return fetcher(apiMaterialsFilters(), setIsLoading);
  };

  const fetchMaterialFolders = async (setIsLoading: Dispatch<SetStateAction<boolean>>, filter?: SearchParamsType) => {
    return fetcher(apiMaterialFolders('', filter), setIsLoading);
  };

  return {
    setFavoriteMaterial,
    removeFavoriteMaterial,
    setFavoriteFolder,
    removeFavoriteFolder,
    fetchAndGroupMaterials,
    fetchAndGroupMaterialsNewApi,
    fetchFilters,
    fetchMaterialFolders,
    fetchMaterialFolderFiles,
  };
};
